import { z } from 'zod'

/**
 * What kind of thing an action is, which is not the same question as what it is
 * about.
 *
 * An opportunity only ends when somebody acts; a regression can end on its own
 * when the number comes back. They share a lifecycle, an evidence shape and a
 * measurement, so they share a table — but the review job has to know which
 * direction "better" points in, and the interface has to know that one of the
 * three is news rather than a job.
 */
export const ACTION_NATURES = ['opportunity', 'regression', 'risk'] as const
export type ActionNature = (typeof ACTION_NATURES)[number]

/**
 * `stale` and `resolved` are both "the detector stopped seeing it", split
 * because the difference is the whole point: a keyword that left the striking
 * band by reaching position three resolved, and one that left by falling to
 * forty did not. Only the detector that emitted the row can tell those apart,
 * so closing carries a reason as well as a state.
 */
export const ACTION_STATES = [
  'open',
  'in_progress',
  'done',
  'dismissed',
  'resolved',
  'stale',
] as const
export type ActionState = (typeof ACTION_STATES)[number]

export const OPEN_ACTION_STATES = ['open', 'in_progress'] as const

/**
 * What an action is *about*. Never a single foreign key, because the second
 * detector written needs two of them: a cannibalisation is one keyword and the
 * pages fighting over it, and a content gap is a topic with no row anywhere.
 * The kind is what `subjectKey` is built from and what the join rows carry.
 */
export const ACTION_SUBJECT_KINDS = [
  'keyword',
  'page',
  'keyword-page',
  'cluster',
  'domain',
  'topic',
  'site',
] as const
export type ActionSubjectKind = (typeof ACTION_SUBJECT_KINDS)[number]

export const ACTION_SUBJECT_ROLES = ['primary', 'related'] as const
export type ActionSubjectRole = (typeof ACTION_SUBJECT_ROLES)[number]

/** Who put the row there. `user` exists so somebody's own task lives in the
 * same log as ours rather than in a second list beside it. */
export const ACTION_SOURCES = ['detector', 'model', 'user'] as const
export type ActionSource = (typeof ACTION_SOURCES)[number]

export const ACTION_EFFORTS = ['low', 'medium', 'high'] as const
export type ActionEffort = (typeof ACTION_EFFORTS)[number]

/**
 * What the timeline is made of. Every state change writes one, and so does
 * every measurement — the row is the state, this is how it got there, and the
 * customer-facing history is a read of these and nothing else.
 */
export const ACTION_EVENT_KINDS = [
  'detected',
  'reopened',
  'enriched',
  'started',
  'completed',
  'verified',
  'dismissed',
  'resolved',
  'stale',
  'measured',
  'note',
] as const
export type ActionEventKind = (typeof ACTION_EVENT_KINDS)[number]

/**
 * The answer to "did it work", which is a different question from "did they do
 * it" — that one is `verifiedAt`, and it is deterministic. `too_early` is a
 * first-class verdict rather than an absent row because a fortnight of silence
 * after a change is a fact about the window, not about the change.
 */
export const ACTION_VERDICTS = [
  'improved',
  'unchanged',
  'declined',
  'too_early',
  'unmeasurable',
] as const
export type ActionVerdict = (typeof ACTION_VERDICTS)[number]

export interface ActionTypeSpec {
  id: string
  nature: ActionNature
  subjectKind: ActionSubjectKind
  /** Shown to the customer as the name of the job. An imperative, because the
   * page is a list of things to do and a noun phrase reads as a report. */
  label: string
  /** The metric the review job measures, and the direction better points in.
   * `position` is the one that improves by going down. */
  metric: 'clicks' | 'impressions' | 'position' | 'ctr'
  improves: 'up' | 'down'
  /** Typical effort, used for ordering before anyone has looked at the row. */
  effort: ActionEffort
  /**
   * Days after the work is verified before an outcome means anything. Search
   * Console lags, and a ranking change takes longer than a crawl — measuring on
   * day three produces a verdict of "unchanged" for everything.
   */
  measureAfterDays: number
  /** Which feeds have to have written something for this check to be able to
   * run at all. A detector whose feeds are dark is skipped and says so, rather
   * than reporting an empty result as a clean bill of health. */
  requires: readonly ('gsc' | 'sitemap' | 'backlinks' | 'serp' | 'scrape')[]
}

/**
 * Every check the deployment knows how to make.
 *
 * Metadata only — no queries, no prompts — because the SPA renders labels from
 * this list and the api validates filters against it, and neither of those can
 * import the worker. The scan, the verification and the measurement live beside
 * the detector in `apps/worker/src/detectors`, keyed by the same id.
 *
 * A closed set on purpose. Per-type verification, per-type interface and the
 * one thing that compounds — how often a *kind* of change actually moved a
 * number, across every site on the deployment — are all only computable while
 * the type is an enum rather than a sentence a model wrote.
 */
export const ACTION_TYPES = [
  {
    id: 'keyword.striking-distance',
    nature: 'opportunity',
    subjectKind: 'keyword',
    label: 'Push a near-miss keyword onto page one',
    metric: 'clicks',
    improves: 'up',
    effort: 'medium',
    measureAfterDays: 28,
    requires: ['gsc'],
  },
  {
    id: 'keyword.ctr-gap',
    nature: 'opportunity',
    subjectKind: 'keyword-page',
    label: 'Rewrite a listing that ranks well but gets ignored',
    metric: 'ctr',
    improves: 'up',
    effort: 'low',
    measureAfterDays: 28,
    requires: ['gsc'],
  },
  {
    id: 'page.cannibalisation',
    nature: 'risk',
    subjectKind: 'keyword-page',
    label: 'Stop two pages competing for one search',
    metric: 'clicks',
    improves: 'up',
    effort: 'high',
    measureAfterDays: 42,
    requires: ['gsc'],
  },
  {
    id: 'page.traffic-drop',
    nature: 'regression',
    subjectKind: 'page',
    label: 'A page lost most of its traffic',
    metric: 'clicks',
    improves: 'up',
    effort: 'medium',
    measureAfterDays: 21,
    requires: ['gsc'],
  },
  {
    id: 'backlink.dead-target',
    nature: 'risk',
    subjectKind: 'page',
    label: 'Recover links pointing at a page that is gone',
    metric: 'clicks',
    improves: 'up',
    effort: 'low',
    measureAfterDays: 42,
    requires: ['backlinks'],
  },
] as const satisfies readonly ActionTypeSpec[]

export type ActionTypeId = (typeof ACTION_TYPES)[number]['id']

export const ACTION_TYPE_IDS = ACTION_TYPES.map((type) => type.id) as ActionTypeId[]

const byId = new Map<string, ActionTypeSpec>(ACTION_TYPES.map((type) => [type.id, type]))

export function actionType(id: string): ActionTypeSpec | null {
  return byId.get(id) ?? null
}

export const actionTypeIdSchema = z.enum(ACTION_TYPE_IDS as [ActionTypeId, ...ActionTypeId[]])
export const actionNatureSchema = z.enum(ACTION_NATURES)
export const actionStateSchema = z.enum(ACTION_STATES)
export const actionEffortSchema = z.enum(ACTION_EFFORTS)
export const actionVerdictSchema = z.enum(ACTION_VERDICTS)

/**
 * How many of a site's open actions get described by a model on one scan.
 *
 * The cap is the money. Detection is a scan and costs the same whether it finds
 * three rows or three hundred; enrichment is a model call each. Everything the
 * detectors find is stored — storage is not the constraint and a row that rises
 * later gets its turn — but only the top of the list is paid for tonight.
 */
export const ACTION_ENRICH_LIMIT = 12

/**
 * How many a customer is shown at once. Not a paging limit: a list of forty
 * jobs is a list nobody starts, and the ordering is the product's opinion, so
 * showing the whole tail would be handing back the one thing this feed decides.
 */
export const ACTION_VISIBLE_LIMIT = 8

/** Purpose slug every action enrichment run is filed under, so spend and volume
 * for this feed are one `group by` in /admin. */
export const ACTION_AI_PURPOSE = 'actions.enrich'

/** The hook name an enrichment run carries, so its answer finds its way back to
 * the action whether it took four seconds or four hours. */
export const ACTION_AI_HOOK = 'action.enrich'
